import {
  Button as MaterialButton,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  makeStyles,
  Paper,
  Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CancelIcon from '@material-ui/icons/Cancel';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import SearchIcon from '@material-ui/icons/Search';
import cn from 'classnames';
import { parse as parseQuery, stringify as stringifyQuery } from 'query-string';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { useHistory, useLocation } from 'react-router';
import { Column, Table } from 'react-virtualized';
import _pick from 'lodash/pick';
import _uniq from 'lodash/uniq';
import matchingRoundsAPI, {
  MatchingRound,
  MatchingRoundId,
  MatchingRoundStatus,
} from '../../api/matching-rounds';
import { useMatchingRoundsContext } from '../../contexts/matching-rounds-context';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { formatDate } from '../../utils/date';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import { AlertState, Link, Select, StatusBadge, Text, TextField } from '../common';
import {
  TableCell,
  TableFooter,
  TableHeadCell,
  TableHeadRow,
  TableRow,
} from '../common/table';
import MatchingRoundsRowActions from '../matching-rounds/matching-rounds-row-actions';

type FilterType = 'search' | 'status';
type Filters = { [key in FilterType]: string };

const STATUS_FILTER_OPTIONS = [
  { label: 'All Statuses', value: 'none' },
  { label: 'Created', value: MatchingRoundStatus.CREATED },
  { label: 'Planned', value: MatchingRoundStatus.PLANNED },
  { label: 'In Review', value: MatchingRoundStatus.IN_APPROVAL },
  { label: 'Finalized', value: MatchingRoundStatus.FINALIZED },
];

const MAX_TITLE_LENGTH = 80;
const PAGE_SIZE = 10;

function getMatchingRoundStatusVariant(
  status: MatchingRoundStatus,
): 'default' | 'success' | 'warning' | 'primary' {
  switch (status) {
    case MatchingRoundStatus.CREATED:
      return 'default';
    case MatchingRoundStatus.PLANNED:
      return 'primary';
    case MatchingRoundStatus.IN_APPROVAL:
      return 'warning';
    case MatchingRoundStatus.FINALIZED:
      return 'success';
    default:
      return 'default';
  }
}

function getMatchingRoundStatusLabel(status: MatchingRoundStatus): string {
  switch (status) {
    case MatchingRoundStatus.IN_APPROVAL:
      return 'In Review';
    default:
      return status.replace('_', ' ');
  }
}

function parseFiltersFromURL(search: string): Filters {
  const keys: FilterType[] = ['search', 'status'];
  const parsedFilter = _pick(parseQuery(search), keys);
  const result = Object.keys(parsedFilter).reduce((acc: Filters, k) => {
    const key = k as FilterType;
    const value = parsedFilter[key];
    if (typeof value === 'string') {
      acc[key] = value;
    }
    return acc;
  }, {} as Filters);

  return result;
}

function getPageFromURL(search: string): number {
  const parsedQuery = _pick(parseQuery(search), ['page']);
  return parsedQuery.page ? Number(parsedQuery.page) : 1;
}

const useStyles = makeStyles({
  container: {
    padding: 0,
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 12px 12px 16px',
    height: 64,
    boxSizing: 'border-box',
  },
  filterList: {
    display: 'flex',
    alignItems: 'center',
  },
  filterItem: {
    '& + &': {
      marginLeft: 15,
    },
  },
  filterSelect: {
    width: 160,
  },
  searchInput: {
    width: '100%',
    maxWidth: 320,
  },
  filterActions: {
    display: 'flex',
    alignItems: 'center',
    margin: '-1px 0',
  },
  filterActionsButton: {
    marginLeft: 11,
  },
  emptyBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 696,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: 500,
  },
  emptyStateBtn: {
    marginTop: 16,
  },
  table: {
    outline: 'none',
  },
  column: {
    display: 'flex',
  },
  row: {},
  justifyContentEnd: {
    justifyContent: 'flex-end',
  },
  titleColumn: {
    paddingRight: 10,
    boxSizing: 'border-box',
  },
  text: {
    cursor: 'pointer',
    color: COLORS.COLOR_BLUE_DARKENED_10,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  containerRemoveFilter: {
    marginLeft: 10,
  },
  iconRemoveFilter: {
    width: 20,
  },
});

function MatchingRoundsTable() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { width: containerWidth, ref: containerRef } = useResizeDetector();

  const { hasAccessToAction } = useContext(UserContext);
  const {
    matchingRounds,
    selectedRoundIds,
    setMatchingRounds,
    setSelectedRoundIds,
    resetKey,
  } = useMatchingRoundsContext();

  const filtersFromURL = useMemo(
    () => parseFiltersFromURL(location.search),
    [location.search],
  );

  const debouncedTimer = useRef<number>();
  const [searchValue, setSearchValue] = useState(filtersFromURL.search || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadError, setLoadError] = useState();
  const [isEmptyState, setIsEmptyState] = useState(false);

  const page = useMemo(
    () => getPageFromURL(location.search),
    [location.search],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      const query = stringifyQuery({
        page: nextPage > 1 ? nextPage : undefined,
        ...filtersFromURL,
      });

      if (query) {
        history.replace(`${location.pathname}?${query}`);
      } else {
        history.replace(location.pathname);
      }
    },
    [filtersFromURL, location.pathname, history],
  );

  const loadMatchingRounds = useCallback(
    async (
      currentFilters: Filters,
      currentPage: number,
      isReload?: boolean,
    ) => {
      try {
        setMatchingRounds([]);
        setIsLoading(true);
        setIsEmptyState(false);

        if (!currentFilters?.search && searchValue.length > 2) {
          setSearchValue('');
        }

        const loaded = await matchingRoundsAPI.getMatchingRounds(
          currentPage - 1,
        );

        // Client-side filtering for search and status
        let filtered = loaded;
        if (currentFilters.status && currentFilters.status !== 'none') {
          filtered = filtered.filter((r) => r.status === currentFilters.status);
        }
        if (currentFilters.search) {
          const searchLower = currentFilters.search.toLowerCase();
          filtered = filtered.filter((r) =>
            r.title?.toLowerCase().includes(searchLower),
          );
        }

        if (filtered.length === 0 && loaded.length === 0) {
          if (isReload && currentPage > 1) {
            handlePageChange(currentPage - 1);
            return;
          } else if (currentPage > 1) {
            handlePageChange(1);
            return;
          }
          setIsLoading(false);
          setIsEmptyState(true);
          return;
        }

        if (filtered.length === 0) {
          setIsLoading(false);
          setIsEmptyState(true);
          return;
        }

        setMatchingRounds(filtered);
        setIsLoading(false);

        if (loaded.length === PAGE_SIZE) {
          setIsNextPageLoading(true);
          try {
            const nextRounds = await matchingRoundsAPI.getMatchingRounds(
              currentPage,
            );
            setIsNextPageLoading(false);
            setHasNextPage(nextRounds.length > 0);
          } catch {
            setIsNextPageLoading(false);
            setHasNextPage(false);
          }
        } else {
          setHasNextPage(false);
        }
      } catch (e: any) {
        console.error('Error loading matching rounds:', e);
        const errorMessage =
          e?.response?.data?.message || 'Internal server error';
        setIsLoading(false);
        setLoadError(errorMessage);
      }
    },
    [setMatchingRounds, handlePageChange, searchValue],
  );

  const handleFilterUpdate = useCallback(
    (field: FilterType, value: string | undefined) => {
      if (!value || value === 'none') {
        history.replace(
          `${location.pathname}?${stringifyQuery({
            ...filtersFromURL,
            [field]: undefined,
          })}`,
        );
      } else if (field === 'search') {
        history.replace(
          `${location.pathname}?${stringifyQuery({ [field]: value })}`,
        );
      } else {
        history.replace(
          `${location.pathname}?${stringifyQuery({
            ...filtersFromURL,
            [field]: value,
            search: undefined,
          })}`,
        );
      }
    },
    [filtersFromURL, history, location.pathname],
  );

  const handleClearFilters = useCallback(() => {
    if (searchValue) {
      setSearchValue('');
    }
    history.replace(location.pathname);
  }, [history, location, searchValue]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      clearTimeout(debouncedTimer.current);
      setSearchValue(e.target.value);

      debouncedTimer.current = setTimeout(() => {
        if (e.target.value.trim().length > 2) {
          handleFilterUpdate('search', e.target.value.trim());
        } else {
          handleFilterUpdate('search', undefined);
        }
      }, 800) as unknown as number;
    },
    [handleFilterUpdate],
  );

  const showClearFilter = useMemo(() => {
    return !!searchValue || Object.values(filtersFromURL).some((v) => !!v);
  }, [filtersFromURL, searchValue]);

  const previousResetKey = useRef(resetKey);

  useEffect(() => {
    let isReload = false;
    if (previousResetKey.current !== resetKey) {
      isReload = true;
      previousResetKey.current = resetKey;
    }
    loadMatchingRounds(filtersFromURL, page, isReload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersFromURL, page, resetKey]);

  const handleAllRowSelect = useCallback(
    (checked: boolean) => {
      if (checked) {
        const ids = matchingRounds.map((r) => r.id);
        return setSelectedRoundIds([...ids]);
      }
      return setSelectedRoundIds([]);
    },
    [matchingRounds, setSelectedRoundIds],
  );

  const handleRowSelect = useCallback(
    (checked: boolean, roundId: MatchingRoundId) => {
      if (checked) {
        return setSelectedRoundIds(_uniq([...selectedRoundIds, roundId]));
      }
      return setSelectedRoundIds(
        selectedRoundIds.filter((id) => id !== roundId),
      );
    },
    [selectedRoundIds, setSelectedRoundIds],
  );

  const handleRowClick = useCallback(
    ({ rowData }: { rowData: MatchingRound }) => {
      const roundId = rowData.id;
      handleRowSelect(!selectedRoundIds.includes(roundId), roundId);
    },
    [selectedRoundIds, handleRowSelect],
  );

  const truncateTitle = (title: string): string => {
    if (!title) return '';
    return title.length > MAX_TITLE_LENGTH
      ? `${title.substring(0, MAX_TITLE_LENGTH)}...`
      : title;
  };

  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  const tableWidth = containerWidth || 500;

  return (
    <Paper className={classes.container} data-testid='matching-rounds-table'>
      <div className={classes.filterContainer}>
        <div className={classes.filterList}>
          <div className={classes.filterItem}>
            <TextField
              className={classes.searchInput}
              value={searchValue}
              onChange={handleSearch}
              placeholder='Search'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              small
              data-testid='filter-search'
            />
          </div>
          <div className={classes.filterItem}>
            <Select
              className={classes.filterSelect}
              value={filtersFromURL.status || STATUS_FILTER_OPTIONS[0].value}
              options={STATUS_FILTER_OPTIONS}
              onSelect={(item) => handleFilterUpdate('status', item?.value)}
              isActive={
                !!filtersFromURL.status &&
                filtersFromURL.status !== STATUS_FILTER_OPTIONS[0].value
              }
            />
          </div>
          {showClearFilter && (
            <Tooltip title='Clear all filters'>
              <IconButton
                data-testid='button-remove-filter'
                className={classes.containerRemoveFilter}
                onClick={() => handleClearFilters()}
              >
                <img
                  src='/filter.svg'
                  alt='Remove filter'
                  className={classes.iconRemoveFilter}
                />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {selectedRoundIds.length > 0 && (
          <div className={classes.filterActions}>
            <Text variant='normal'>{selectedRoundIds.length} selected</Text>
            {hasAccessToAction('matchingRound.details.update') && (
              <Tooltip title='Initiate'>
                <IconButton
                  className={cn(
                    classes.filterActionsButton,
                    CLASS_TRACKING.INTERNAL_ACTION,
                  )}
                  data-testid='bulk-action-initiate'
                  onClick={() => { }}
                >
                  <PlayArrowIcon />
                </IconButton>
              </Tooltip>
            )}
            {hasAccessToAction('matchingRound.details.update') && (
              <Tooltip title='Cancel'>
                <IconButton
                  className={cn(
                    classes.filterActionsButton,
                    CLASS_TRACKING.INTERNAL_ACTION,
                  )}
                  data-testid='bulk-action-cancel'
                  onClick={() => { }}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      <div ref={containerRef}>
        {matchingRounds.length > 0 ? (
          <div>
            <Table
              width={tableWidth}
              height={
                64 * (matchingRounds.length > 10 ? matchingRounds.length : 10) +
                56
              }
              headerHeight={56}
              rowHeight={64}
              rowCount={matchingRounds.length}
              rowGetter={({ index }) => matchingRounds[index]}
              rowClassName={classes.row}
              gridClassName={classes.table}
              onRowClick={
                hasAccessToAction('matchingRound.details.update')
                  ? handleRowClick
                  : () => { }
              }
              headerRowRenderer={(props) => (
                <TableHeadRow {...props} />
              )}
              rowRenderer={(props) => (
                <TableRow
                  selected={selectedRoundIds.includes(props.rowData.id)}
                  {...props}
                />
              )}
            >
              <Column
                dataKey='id'
                className={classes.column}
                width={
                  hasAccessToAction('matchingRound.details.update') ? 72 : 20
                }
                minWidth={
                  hasAccessToAction('matchingRound.details.update') ? 72 : 20
                }
                headerRenderer={() => (
                  <div>
                    {hasAccessToAction('matchingRound.details.update') && (
                      <Checkbox
                        checked={
                          selectedRoundIds.length === matchingRounds.length &&
                          matchingRounds.length > 0
                        }
                        color='primary'
                        indeterminate={
                          selectedRoundIds.length > 0 &&
                          selectedRoundIds.length !== matchingRounds.length
                        }
                        onChange={(e) => handleAllRowSelect(e.target.checked)}
                      />
                    )}
                  </div>
                )}
                cellRenderer={({ cellData }) => (
                  <div>
                    {hasAccessToAction('matchingRound.details.update') && (
                      <Checkbox
                        data-testid='matching-round-table-row-checkbox'
                        checked={selectedRoundIds.includes(cellData)}
                        color='primary'
                        onChange={(e) =>
                          handleRowSelect(e.target.checked, cellData)
                        }
                      />
                    )}
                  </div>
                )}
              />
              <Column
                dataKey='title'
                className={classes.column}
                width={200}
                minWidth={200}
                flexGrow={1}
                headerRenderer={() => (
                  <TableHeadCell>Title</TableHeadCell>
                )}
                cellRenderer={({ rowData }: { rowData: MatchingRound }) => (
                  <TableCell className={classes.titleColumn} truncated>
                    <Link
                      className={classes.text}
                      to={getRoutePath(Pages.MATCHING_ROUND_DETAILS, {
                        roundId: rowData.id,
                      })}
                    >
                      {truncateTitle(rowData.title)}
                    </Link>
                  </TableCell>
                )}
              />
              <Column
                dataKey='status'
                className={classes.column}
                width={100}
                minWidth={100}
                maxWidth={150}
                flexGrow={1}
                headerRenderer={() => <TableHeadCell>Status</TableHeadCell>}
                cellRenderer={({ rowData }: { rowData: MatchingRound }) => (
                  <StatusBadge
                    variant={getMatchingRoundStatusVariant(rowData.status)}
                    status={getMatchingRoundStatusLabel(rowData.status)}
                    data-testid='status-badge'
                  />
                )}
              />
              <Column
                dataKey='creationDate'
                className={classes.column}
                width={120}
                minWidth={100}
                headerRenderer={() => (
                  <TableHeadCell>Creation Date</TableHeadCell>
                )}
                cellRenderer={({ rowData }: { rowData: MatchingRound }) => (
                  <TableCell truncated>
                    {formatDate(rowData.creationDate)}
                  </TableCell>
                )}
              />
              <Column
                dataKey='actions'
                className={cn(classes.column, classes.justifyContentEnd)}
                width={40}
                minWidth={40}
                headerRenderer={() => <div></div>}
                cellRenderer={({ rowData }: { rowData: MatchingRound }) => (
                  <TableCell onClick={stopPropagation}>
                    <MatchingRoundsRowActions round={rowData} />
                  </TableCell>
                )}
              />
            </Table>
            <TableFooter
              page={page}
              onPageChange={handlePageChange}
              isLoading={isNextPageLoading}
              hasNextPage={hasNextPage}
            />
          </div>
        ) : (
          <>
            {isEmptyState ? (
              <div className={classes.emptyBlock}>
                <div className={classes.emptyState}>
                  <Text variant='normal'>
                    This panel contains a list of matching rounds.
                    <br />
                    {hasAccessToAction('matchingRound.create') &&
                      'We were not able to find information you requested, but feel free to add something new!'}
                  </Text>
                  {hasAccessToAction('matchingRound.create') && (
                    <MaterialButton
                      component={Link}
                      to={Pages.NEW_MATCHING_ROUND}
                      className={classes.emptyStateBtn}
                      startIcon={<AddIcon />}
                      variant='contained'
                      color='primary'>
                      New Round
                    </MaterialButton>
                  )}
                </div>
              </div>
            ) : (
              <div className={classes.emptyBlock}>
                {isLoading && <CircularProgress size={36} color='primary' />}
                {!isLoading && !!loadError && (
                  <AlertState type='error'>{loadError}</AlertState>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Paper>
  );
}

export default MatchingRoundsTable;
